import type { DeviceBranch, DeviceSession } from '@/api/models';
import { isJwtExpired } from './jwt';

/**
 * The **device** session: the long-lived, per-branch device token plus the
 * branch identity it was minted for, obtained by exchanging the branch's static
 * API token via `POST /auth/device`. It authorizes only the roster + PIN
 * endpoints. Stored under its own key, independent of the per-shift seller
 * session (see `authSession.ts`) — a seller locking their shift keeps the
 * terminal enrolled.
 */
const DEVICE_SESSION_STORAGE_KEY = 'bos.auth.device';

const hasWindowStorage = () =>
    typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const readDeviceSession = (): DeviceSession | null => {
    if (!hasWindowStorage()) {
        return null;
    }
    const rawValue = window.localStorage.getItem(DEVICE_SESSION_STORAGE_KEY);
    if (!rawValue) {
        return null;
    }
    try {
        const parsed = JSON.parse(rawValue) as Partial<DeviceSession>;
        if (
            parsed &&
            typeof parsed.deviceToken === 'string' &&
            parsed.deviceToken.length > 0 &&
            parsed.branch &&
            typeof parsed.branch.id === 'string' &&
            typeof parsed.branch.name === 'string'
        ) {
            return parsed as DeviceSession;
        }
    } catch {
        // Fall through to the cleanup below.
    }
    window.localStorage.removeItem(DEVICE_SESSION_STORAGE_KEY);
    return null;
};

export const getDeviceSession = (): DeviceSession | null => readDeviceSession();

export const getDeviceToken = (): string | null =>
    readDeviceSession()?.deviceToken ?? null;

export const getDeviceBranch = (): DeviceBranch | null =>
    readDeviceSession()?.branch ?? null;

export const setDeviceSession = (session: DeviceSession): void => {
    if (!hasWindowStorage()) {
        return;
    }
    window.localStorage.setItem(
        DEVICE_SESSION_STORAGE_KEY,
        JSON.stringify(session),
    );
};

export const clearDeviceSession = (): void => {
    if (!hasWindowStorage()) {
        return;
    }
    window.localStorage.removeItem(DEVICE_SESSION_STORAGE_KEY);
};

/**
 * `true` when a device session exists and its device JWT has not expired. A
 * rotated branch token / expired device token reads as not-enrolled, sending
 * the terminal back to the enrollment screen.
 */
export const isDeviceEnrolled = (): boolean => !isJwtExpired(getDeviceToken());
